import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FaLock, FaTimes } from 'react-icons/fa';
import {
  createOrder,
  initiateRazorpayPaymentForOrder,
  verifyRazorpayPayment,
} from '../../../api/ordersApi';
import { ApiError } from '../../../api/client';
import { useCart } from '../../../context/CartContext';
import { useStoreStatus } from '../../../context/StoreStatusContext';
import {
  loadRazorpayCheckoutScript,
  openRazorpayCheckout,
  unlockRazorpayCheckout,
} from '../../../utils/razorpayCheckout';
import { buildRazorpayCallbackUrl } from './razorpayReturnUtils';
import { stripPhoneDigits } from '../../../utils/phone';
import { computeOrderGrandTotal } from '../../Cart/cartCouponUtils';
import { clearStoredAppliedCoupon } from '../../Cart/cartCouponStorage';
import { useCartCharges } from '../../Cart/useCartCharges';
import AccountSessionError from '../AccountSessionError';
import { buildAuthRequiredRedirectUrl, readStoredAuth } from '../profileFormUtils';
import PaymentBillDetails from './PaymentBillDetails';
import PaymentFulfillmentSummary, { formatDeliveryAddressLines } from './PaymentFulfillmentSummary';
import PaymentTrustStrip from './PaymentTrustStrip';
import {
  buildCreateOrderRequest,
  mapPaymentMethodForApi,
  validateCreateOrderPayload,
} from './buildCreateOrderRequest';
import {
  getDefaultPaymentMethodId,
  razorpayMethodConfig,
} from './paymentMethodUtils';
import {
  clearStoredCheckoutPayload,
  persistCheckoutPayload,
  resolveCheckoutPayload,
} from './paymentCheckoutState';
import {
  buildPendingOrderPaymentFromCheckout,
  clearStoredPendingOrderPayment,
  persistPendingOrderPayment,
  resolvePaymentPageSession,
} from './pendingOrderPaymentState';
import { recordPaymentFailure } from './paymentFailureState';
import '../../Checkout/Checkout.css';
import './Payment.css';

/**
 * @param {{
 *   token: string,
 *   orderNumber: string,
 *   rzpKeyId: string,
 *   rzpOrderID: string,
 *   prefill: { name?: string, email?: string, contact?: string },
 *   paymentMethodId: string,
 *   onCheckoutOpen?: () => void,
 * }} params
 */
/** @param {'creating-order' | 'opening-gateway'} phase */
function payPhaseLabel(phase) {
  if (phase === 'creating-order') return 'Creating order…';
  return 'Opening payment…';
}

/** @param {'creating-order' | 'opening-gateway'} phase */
function payPhaseOverlayCopy(phase) {
  if (phase === 'creating-order') {
    return {
      title: 'Creating your order',
      detail: 'Confirming your items and charges. This usually takes a few seconds.',
    };
  }
  return {
    title: 'Opening secure payment',
    detail: 'Loading Razorpay checkout. Choose UPI, card, or other options in the next screen.',
  };
}

async function runRazorpayCheckoutAndVerify({
  token,
  orderNumber,
  rzpKeyId,
  rzpOrderID,
  prefill,
  paymentMethodId,
  onCheckoutOpen,
}) {
  const method = razorpayMethodConfig(paymentMethodId);
  const callbackUrl = buildRazorpayCallbackUrl(orderNumber);
  const paymentResponse = await openRazorpayCheckout({
    key: rzpKeyId,
    name: 'Digirasa',
    description: `Payment for order ${orderNumber}`,
    order_id: rzpOrderID,
    prefill,
    ...(method ? { method } : {}),
    ...(callbackUrl
      ? {
          callback_url: callbackUrl,
        }
      : {}),
    ...(onCheckoutOpen ? { onCheckoutOpen } : {}),
  });

  await verifyRazorpayPayment({
    token,
    orderNumber,
    rzpOrderID: paymentResponse.razorpay_order_id,
    rzpPaymentId: paymentResponse.razorpay_payment_id,
    razorpaySignature: paymentResponse.razorpay_signature,
    paymentMethod: mapPaymentMethodForApi(paymentMethodId),
  });
}

const Payment = () => {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { cartItems, cartTotal: liveCartTotal, clearCart } = useCart();
  const { isKitchenOpen } = useStoreStatus();
  const { token, user } = readStoredAuth();

  const paymentMethodId = getDefaultPaymentMethodId();
  const [payload, setPayload] = useState(() => {
    const session = resolvePaymentPageSession(locationState);
    if (session.mode === 'checkout' && session.checkoutPayload) {
      return session.checkoutPayload;
    }
    return resolveCheckoutPayload(locationState);
  });
  const [pendingOrderPayment, setPendingOrderPayment] = useState(() => {
    const session = resolvePaymentPageSession(locationState);
    return session.mode === 'resume' ? session.pendingOrderPayment : null;
  });
  const [paying, setPaying] = useState(false);
  /** @type {[null | 'creating-order' | 'opening-gateway', Function]} */
  const [payPhase, setPayPhase] = useState(null);
  const [payOverlayVisible, setPayOverlayVisible] = useState(false);
  const [payError, setPayError] = useState('');

  const isResumeMode = Boolean(pendingOrderPayment?.orderNumber);

  useEffect(() => {
    const session = resolvePaymentPageSession(locationState);

    if (session.mode === 'checkout' && session.checkoutPayload) {
      clearStoredPendingOrderPayment();
      setPendingOrderPayment(null);
      setPayload(session.checkoutPayload);
      persistCheckoutPayload(session.checkoutPayload);
      return;
    }

    if (session.mode === 'resume' && session.pendingOrderPayment) {
      setPendingOrderPayment(session.pendingOrderPayment);
      persistPendingOrderPayment(session.pendingOrderPayment);
      return;
    }

    setPendingOrderPayment(null);
    const resolved = resolveCheckoutPayload(locationState);
    if (resolved) {
      setPayload(resolved);
      persistCheckoutPayload(resolved);
    }
  }, [locationState]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Payment | DigiRasa';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await unlockRazorpayCheckout();
      if (!cancelled) {
        loadRazorpayCheckoutScript().catch(() => {
          /* surfaced when user taps Pay */
        });
      }
    })();

    const onPageShow = () => {
      unlockRazorpayCheckout().catch(() => {});
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      cancelled = true;
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const displayCartItems = useMemo(() => {
    if (isResumeMode && pendingOrderPayment) {
      return pendingOrderPayment.cartItems;
    }
    if (cartItems.length > 0) {
      return cartItems.map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image,
        category: item.category,
      }));
    }
    return payload?.cartItems ?? [];
  }, [cartItems, payload, isResumeMode, pendingOrderPayment]);

  const cartTotal = isResumeMode
    ? pendingOrderPayment?.charges.cartTotal ?? 0
    : liveCartTotal > 0
      ? liveCartTotal
      : (payload?.charges.cartTotal ?? 0);

  const fulfillmentType = isResumeMode
    ? pendingOrderPayment?.fulfillmentType ?? 'delivery'
    : (payload?.form.fulfillmentType ?? 'delivery');
  const appliedCoupon = isResumeMode ? null : (payload?.appliedCoupon ?? null);
  const distanceKm =
    isResumeMode || fulfillmentType === 'pickup' ? 0 : (payload?.distanceKm ?? null);

  const chargesHook = useCartCharges({
    cartItems: displayCartItems,
    cartTotal,
    fulfillmentType,
    appliedCoupon,
    distanceKm,
    enabled: Boolean(!isResumeMode && token && payload && displayCartItems.length > 0),
  });

  const {
    packagingFee,
    delivery,
    gstTax,
    discount,
    deferDeliveryAndTax,
    status: chargesStatus,
    error: chargesError,
  } = chargesHook;

  const grandTotal = isResumeMode
    ? pendingOrderPayment?.charges.grandTotal ?? 0
    : computeOrderGrandTotal({
        cartTotal,
        discount,
        packagingFee,
        delivery,
        gstTax,
        deferDeliveryAndTax,
      });

  const summaryCharges = useMemo(() => {
    if (isResumeMode && pendingOrderPayment) {
      return pendingOrderPayment.charges;
    }
    if (chargesStatus === 'ready' && !deferDeliveryAndTax) {
      return {
        cartTotal,
        discount,
        packagingFee,
        delivery,
        gstTax,
        grandTotal,
      };
    }
    if (payload?.charges) {
      return payload.charges;
    }
    return {
      cartTotal,
      discount: 0,
      packagingFee: 0,
      delivery: 0,
      gstTax: 0,
      grandTotal: cartTotal,
    };
  }, [
    isResumeMode,
    pendingOrderPayment,
    chargesStatus,
    deferDeliveryAndTax,
    payload,
    cartTotal,
    discount,
    packagingFee,
    delivery,
    gstTax,
    grandTotal,
  ]);

  const totalsDrift =
    !isResumeMode &&
    chargesStatus === 'ready' &&
    !deferDeliveryAndTax &&
    payload?.charges &&
    Math.abs(grandTotal - payload.charges.grandTotal) > 0.02;

  const goToPaymentFailed = (message, pendingPayment) => {
    if (pendingPayment?.orderNumber) {
      recordPaymentFailure(pendingPayment.orderNumber);
      persistPendingOrderPayment(pendingPayment);
    }
    navigate('/payment/failed', {
      replace: true,
      state: {
        message,
        pendingOrderPayment: pendingPayment ?? undefined,
        orderNumber: pendingPayment?.orderNumber,
        grandTotal:
          pendingPayment?.charges?.grandTotal ?? pendingPayment?.grandTotal ?? undefined,
      },
    });
  };

  const resolvePrefill = () => {
    if (isResumeMode && pendingOrderPayment?.contact) {
      const { name, email, phone } = pendingOrderPayment.contact;
      return {
        name: name || undefined,
        email: email || undefined,
        contact: stripPhoneDigits(phone || '') || undefined,
      };
    }
    if (payload) {
      return {
        name: payload.form.fullName || undefined,
        email: payload.form.email || undefined,
        contact: stripPhoneDigits(payload.form.phone) || undefined,
      };
    }
    return {
      name: user?.name || user?.fullName || undefined,
      email: user?.email || undefined,
      contact: stripPhoneDigits(user?.phone || user?.phone_number || '') || undefined,
    };
  };

  if (!token) {
    return <Navigate to={buildAuthRequiredRedirectUrl('/payment', { reauth: true })} replace />;
  }

  if (!isResumeMode && !isKitchenOpen) {
    return <Navigate to="/cart" replace />;
  }

  if (!isResumeMode && !payload) {
    if (cartItems.length === 0) {
      return (
        <div className="payment-page">
          <section className="payment-missing">
            <p>
              No checkout details found. <Link to="/cart">Return to cart</Link> or{' '}
              <Link to="/our-menu">browse the menu</Link>.
            </p>
          </section>
        </div>
      );
    }
    return <Navigate to="/checkout" replace state={{ paymentResume: true }} />;
  }

  if (displayCartItems.length === 0) {
    return (
      <div className="payment-page">
        <section className="payment-empty">
          <p>
            Nothing to pay for. <Link to="/our-menu">Browse menu</Link>
          </p>
        </section>
      </div>
    );
  }

  const contactLine = isResumeMode
    ? pendingOrderPayment?.contactLine || ''
    : [payload.form.fullName, payload.form.phone, payload.form.email].filter(Boolean).join(' · ');

  const deliveryAddressLines =
    fulfillmentType === 'delivery'
      ? isResumeMode && pendingOrderPayment?.addressLines?.length
        ? pendingOrderPayment.addressLines
        : payload?.form
          ? formatDeliveryAddressLines(payload.form)
          : []
      : [];

  const payDisabled = isResumeMode
    ? paying
    : paying || chargesStatus === 'loading' || deferDeliveryAndTax || chargesStatus === 'error';

  const chargesLoading = !isResumeMode && chargesStatus === 'loading';

  const handlePayClick = async () => {
    if (!token || paying || payDisabled) return;
    if (!isResumeMode && !isKitchenOpen) return;
    if (!isResumeMode && !payload) return;

    if (!isResumeMode) {
      const validationError = validateCreateOrderPayload(payload);
      if (validationError) {
        setPayError(validationError);
        return;
      }
    }

    setPayError('');
    setPaying(true);
    setPayPhase(isResumeMode ? 'opening-gateway' : 'creating-order');
    setPayOverlayVisible(true);

    /** @type {{ orderId: string | null, orderNumber: string | null, rzpOrderID: string | null, rzpKeyId: string | null } | null} */
    let paymentInit = null;
    /** @type {import('./pendingOrderPaymentState').PendingOrderPayment | null} */
    let resumeContext = isResumeMode ? pendingOrderPayment : null;

    try {
      if (isResumeMode && pendingOrderPayment) {
        paymentInit = await initiateRazorpayPaymentForOrder({
          token,
          orderNumber: pendingOrderPayment.orderNumber,
        });
        resumeContext = pendingOrderPayment;
      } else if (payload) {
        const orderBody = buildCreateOrderRequest(payload, summaryCharges, paymentMethodId);
        paymentInit = await createOrder({ token, body: orderBody });
        const pending = buildPendingOrderPaymentFromCheckout(paymentInit, payload);
        if (pending) {
          resumeContext = pending;
          setPendingOrderPayment(pending);
          persistPendingOrderPayment(pending);
        }
        clearCart();
        clearStoredCheckoutPayload();
        clearStoredAppliedCoupon();
      }

      if (!paymentInit?.orderNumber || !paymentInit.rzpOrderID) {
        throw new Error('Payment details are missing. Please try again from My Orders.');
      }

      if (!paymentInit.rzpKeyId) {
        throw new Error(
          'Payment gateway is not configured on the server. Please contact support or try again later.',
        );
      }

      setPayPhase('opening-gateway');

      await runRazorpayCheckoutAndVerify({
        token,
        orderNumber: paymentInit.orderNumber,
        rzpKeyId: paymentInit.rzpKeyId,
        rzpOrderID: paymentInit.rzpOrderID,
        prefill: resolvePrefill(),
        paymentMethodId,
        onCheckoutOpen: () => setPayOverlayVisible(false),
      });

      if (isResumeMode) {
        clearStoredPendingOrderPayment();
      } else {
        clearCart();
        clearStoredCheckoutPayload();
        clearStoredAppliedCoupon();
      }

      navigate('/payment/success', {
        replace: true,
        state: {
          orderNumber: paymentInit.orderNumber,
          orderId: paymentInit.orderId,
          grandTotal:
            resumeContext?.charges?.grandTotal ??
            summaryCharges.grandTotal,
        },
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Payment could not be completed. Please try again.';

      if (paymentInit?.orderNumber) {
        const failedPending =
          resumeContext ||
          (pendingOrderPayment?.orderNumber === paymentInit.orderNumber
            ? pendingOrderPayment
            : {
                orderNumber: paymentInit.orderNumber,
                grandTotal:
                  summaryCharges?.grandTotal ??
                  pendingOrderPayment?.grandTotal ??
                  undefined,
              });
        goToPaymentFailed(message, failedPending);
        return;
      }

      setPayError(message);
    } finally {
      setPaying(false);
      setPayPhase(null);
      setPayOverlayVisible(false);
      unlockRazorpayCheckout().catch(() => {});
    }
  };

  const activePayLabel = payPhase ? payPhaseLabel(payPhase) : undefined;
  const payOverlayCopy = payPhase ? payPhaseOverlayCopy(payPhase) : null;

  const billProps = {
    cartItems: displayCartItems,
    fulfillmentType,
    appliedCoupon,
    charges: summaryCharges,
    contactLine,
    loading: chargesLoading,
    isResumeMode,
  };

  const fulfillmentProps = {
    fulfillmentType,
    addressLines: deliveryAddressLines,
    contactLine,
    loading: chargesLoading,
    payDisabled,
    onPayClick: handlePayClick,
    payLabel: activePayLabel,
    grandTotal: summaryCharges.grandTotal,
    isResumeMode,
  };

  return (
    <div className="payment-page">
      <div className="payment-shell">
        <section className="payment-section" aria-labelledby="payment-page-title">
          <h1 id="payment-page-title" className="visually-hidden">
            Payment
          </h1>

          {isResumeMode ? (
            <div className="payment-resume-banner" role="status">
              <p className="payment-resume-banner__main">
                Completing payment for order{' '}
                <strong>#{pendingOrderPayment?.orderNumber}</strong>
              </p>
              <p className="payment-resume-banner__hint">
                Your order is saved — tap Pay to complete payment, or open My Orders anytime.
              </p>
            </div>
          ) : null}

          {chargesLoading ? (
            <p className="payment-charges-banner" role="status">
              Updating totals with the latest delivery and tax from our kitchen…
            </p>
          ) : null}

          {!isResumeMode && chargesError ? (
            <AccountSessionError
              message={chargesError}
              returnPath="/payment"
              signInSuffix="to pay for your order"
              variant="block"
            />
          ) : null}

          {payError ? (
            <div
              className="payment-charges-banner payment-charges-banner--warn payment-charges-banner--dismissible"
              role="alert"
            >
              <p className="payment-charges-banner__text">{payError}</p>
              <button
                type="button"
                className="payment-charges-banner__close"
                aria-label="Dismiss error"
                onClick={() => setPayError('')}
              >
                <FaTimes aria-hidden />
              </button>
            </div>
          ) : null}

          {totalsDrift ? (
            <p className="payment-charges-banner payment-charges-banner--warn" role="status">
              Totals were refreshed — the amount below reflects the latest calculation.
            </p>
          ) : null}

          <PaymentTrustStrip />

          <div className="payment-layout">
            <div className="payment-main">
              <PaymentFulfillmentSummary {...fulfillmentProps} />

              <div className="payment-bill-mobile">
                <PaymentBillDetails {...billProps} variant="collapsible" />
              </div>
            </div>

            <div className="payment-aside">
              <PaymentBillDetails {...billProps} variant="summary" />
            </div>
          </div>
        </section>
      </div>

      <div className="payment-mobile-bar">
        <button
          type="button"
          className="payment-mobile-bar__pay payment-mobile-bar__pay--full"
          disabled={payDisabled}
          onClick={handlePayClick}
          aria-busy={paying || undefined}
        >
          <FaLock aria-hidden />
          {activePayLabel || `Pay ₹${summaryCharges.grandTotal.toFixed(2)}`}
        </button>
      </div>

      {payOverlayVisible && payOverlayCopy ? (
        <div
          className="checkout-redirect-overlay payment-paying-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="payment-paying-title"
          aria-describedby="payment-paying-detail"
        >
          <div className="checkout-redirect-box payment-paying-overlay__box">
            <div className="checkout-redirect__spinner" aria-hidden />
            <h3 id="payment-paying-title">{payOverlayCopy.title}</h3>
            <p id="payment-paying-detail">{payOverlayCopy.detail}</p>
            <p className="payment-paying-overlay__amount">
              Total: <strong>₹{summaryCharges.grandTotal.toFixed(2)}</strong>
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Payment;
